export const positiveVariables = [
  "advantage",
  "information",
  "timing",
  "position",
  "energy",
  "harmony"
];

export const dragVariables = [
  "cost",
  "risk",
  "uncertainty",
  "exposure",
  "disorder"
];

export const defaultWeights = {
  advantage: 1,
  information: 1,
  timing: 1,
  position: 1,
  energy: 1,
  harmony: 1,
  cost: 1,
  risk: 1,
  uncertainty: 1,
  exposure: 1,
  disorder: 1
};

export function clamp01(value) {
  if (Number.isNaN(Number(value))) return 0;
  return Math.min(1, Math.max(0, Number(value)));
}

export function weightedMean(values, keys, weights = defaultWeights) {
  const weighted = keys.reduce(
    (state, key) => {
      const weight = Math.max(0, Number(weights[key] ?? 1));
      return {
        total: state.total + clamp01(values[key]) * weight,
        weight: state.weight + weight
      };
    },
    { total: 0, weight: 0 }
  );

  return weighted.weight === 0 ? 0 : weighted.total / weighted.weight;
}

export function evaluateHardGate(option) {
  const failed = (option.gates ?? []).filter((gate) => gate.required && !gate.passed);
  return {
    value: failed.length === 0 ? 1 : 0,
    failed
  };
}

export function scoreOption(option, weights = defaultWeights) {
  const gate = evaluateHardGate(option);
  const positiveForce = weightedMean(option.variables, positiveVariables, weights);
  const drag = weightedMean(option.variables, dragVariables, weights);
  const rawScore = gate.value * (positiveForce / (1 + drag));
  const score = Number(rawScore.toFixed(4));

  return {
    id: option.id,
    label: option.label,
    score,
    scorePercent: Math.round(score * 100),
    gate,
    positiveForce: Number(positiveForce.toFixed(4)),
    drag: Number(drag.toFixed(4)),
    variables: { ...option.variables },
    evidence: option.evidence ?? [],
    missingEvidence: option.missingEvidence ?? [],
    nextAction: option.nextAction
  };
}

export function rankOptions(options, weights = defaultWeights) {
  return options
    .map((option) => scoreOption(option, weights))
    .sort((a, b) => b.score - a.score || a.label.localeCompare(b.label));
}

export function buildDecisionReceipt({ domain, objective, options, weights = defaultWeights }) {
  const ranked = rankOptions(options, weights);
  const winner = ranked[0] ?? null;
  const blocked = ranked.filter((option) => option.gate.value === 0);
  const needsEvidence = ranked.filter((option) => option.missingEvidence.length > 0);

  return {
    version: "strategy-kernel-0.1.0",
    createdAt: new Date().toISOString(),
    domain,
    objective,
    weights: { ...weights },
    winner,
    ranked,
    blocked,
    needsEvidence,
    claimBoundary:
      "Decision scores are operator-model outputs. They are not proof, legal advice, financial advice, or verified corpus counts."
  };
}
